import React, { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { AppView } from "../../types";

type ConversationContext = {
  conversationId: string | null;
  initialPrompt: string | null;
  exploreId: string | null;
  modelName: string | null;
};

type AnalyticsAIContextType = {
  // State
  isSideBarCollapsed: boolean;
  isChatCollapsed: boolean;
  view: AppView;
  conversationContext: ConversationContext;
  arenaId: number;
  userId: number;

  // Actions
  toggleSideBarCollapse: () => void;
  toggleChatCollapse: () => void;
  startNewChat: () => void;
  loadConversation: (id: string) => void;
  submitPrompt: (prompt: string) => void;
  submitSample: (prompt: string, exploreId: string | null, modelName: string | null) => void;
};

const AnalyticsAIContext = createContext<AnalyticsAIContextType | undefined>(undefined);

type AnalyticsAIProviderProps = {
  children: ReactNode;
  arenaId: number;
  userId: number;
};

export const AnalyticsAIProvider: React.FC<AnalyticsAIProviderProps> = ({
  children,
  arenaId,
  userId,
}) => {
  const [isSideBarCollapsed, setIsSideBarCollapsed] = useState<boolean>(true);
  const [isChatCollapsed, setIsChatCollapsed] = useState<boolean>(false);
  const [view, setView] = useState<AppView>("query");
  const [conversationContext, setConversationContext] = useState<ConversationContext>({
    conversationId: null,
    initialPrompt: null,
    exploreId: null,
    modelName: null,
  });

  const toggleSideBarCollapse = useCallback(() => {
    setIsSideBarCollapsed((prev) => !prev);
  }, []);

  const toggleChatCollapse = useCallback(() => {
    setIsChatCollapsed((prev) => !prev);
  }, []);

  const startNewChat = useCallback(() => {
    setView("query");
    setIsSideBarCollapsed(true);
    setConversationContext({
      conversationId: null,
      initialPrompt: null,
      exploreId: null,
      modelName: null,
    });
  }, []);

  const loadConversation = useCallback((id: string) => {
    setView("chat");
    setConversationContext({
      conversationId: id,
      initialPrompt: null,
      exploreId: null,
      modelName: null,
    });
  }, []);

  const submitPrompt = useCallback((prompt: string) => {
    setView("chat");
    setConversationContext({
      conversationId: null,
      initialPrompt: prompt,
      exploreId: null,
      modelName: null,
    });
  }, []);

  const submitSample = useCallback(
    (prompt: string, exploreId: string | null, modelName: string | null) => {
      setView("chat");
      setConversationContext({
        conversationId: null,
        initialPrompt: prompt,
        exploreId,
        modelName,
      });
    },
    [],
  );

  const value: AnalyticsAIContextType = {
    isSideBarCollapsed,
    isChatCollapsed,
    view,
    conversationContext,
    arenaId,
    userId,
    toggleSideBarCollapse,
    toggleChatCollapse,
    startNewChat,
    loadConversation,
    submitPrompt,
    submitSample,
  };

  return <AnalyticsAIContext.Provider value={value}>{children}</AnalyticsAIContext.Provider>;
};

export const useAnalyticsAI = (): AnalyticsAIContextType => {
  const context = useContext(AnalyticsAIContext);
  if (context === undefined) {
    throw new Error("useAnalyticsAI must be used within an AnalyticsAIProvider");
  }
  return context;
};
